import { MessageRole, ToolName } from "../types";
import { ChatMessage } from "../stores/types";

import type { ConversationMessage } from "@/queries/vibe/useRecentVibeChatSessions";

const textContent = (text: string): { type: "text"; text: string }[] =>
  text ? [{ type: "text" as const, text }] : [];

interface ToolCallEntry {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}

interface ToolResultEntry {
  id: string;
  content: string;
}

const internalReturnResultsContent = (): string =>
  JSON.stringify({
    success: true,
    tool_name: ToolName.RETURN_RESULTS,
    internal: true,
    synthetic: true,
    status: "not_required",
    message:
      "No real tool response is required for return_results; it only finalizes the assistant response.",
  });

const collectToolCallsAndResults = (
  messages: ChatMessage[],
  startIndex: number,
): { toolCalls: ToolCallEntry[]; toolResults: ToolResultEntry[]; nextIndex: number } => {
  const toolCalls: ToolCallEntry[] = [];
  const toolResults: ToolResultEntry[] = [];
  let j = startIndex;

  while (j < messages.length) {
    const m = messages[j];
    if (m.role === MessageRole.TOOL_CALL && m.toolCall) {
      const { id: rawId, name, input, output, status } = m.toolCall;
      const id = rawId || `tool-${Date.now()}-${j}`;
      toolCalls.push({
        id,
        type: "function",
        function: {
          name,
          arguments: JSON.stringify(input || {}),
        },
      });
      if (output !== undefined && output !== null) {
        const content = typeof output === "string" ? output : JSON.stringify(output);
        toolResults.push({ id, content });
      } else if (name === ToolName.RETURN_RESULTS) {
        toolResults.push({ id, content: internalReturnResultsContent() });
      } else if (status === "failed" || status === "error") {
        toolResults.push({
          id,
          content: JSON.stringify({
            error: m.toolCall.error || "Cancelled",
            status,
          }),
        });
      } else {
        toolResults.push({
          id,
          content: JSON.stringify({ error: "Cancelled", status: "cancelled" }),
        });
      }
      j += 1;
      continue;
    }
    if (m.role === MessageRole.COMPUTER_AGENT && m.computerAgent) {
      const lastIsComputerAgent =
        toolCalls.length > 0 &&
        toolCalls[toolCalls.length - 1].function.name === ToolName.COMPUTER_AGENT;
      if (!lastIsComputerAgent) {
        const { progressPhrase, results, status: agentStatus, error } = m.computerAgent;
        const id = `computer-agent-${Date.now()}-${j}`;
        const phrase = progressPhrase || "Computer agent";
        toolCalls.push({
          id,
          type: "function",
          function: {
            name: ToolName.COMPUTER_AGENT,
            arguments: JSON.stringify({
              task_description: phrase,
            }),
          },
        });
        const content =
          results && Object.keys(results).length > 0
            ? JSON.stringify(results)
            : JSON.stringify({
                status: agentStatus,
                ...(error && { error }),
              });
        toolResults.push({ id, content });
      }
      j += 1;
      continue;
    }
    break;
  }

  return { toolCalls, toolResults, nextIndex: j };
};

export const convertMessagesToConversationHistory = (
  messages: ChatMessage[],
): ConversationMessage[] => {
  const result: ConversationMessage[] = [];
  let i = 0;

  while (i < messages.length) {
    const msg = messages[i];

    if (msg.role === MessageRole.USER) {
      result.push({
        role: "user",
        content: textContent(msg.content || ""),
      });
      i += 1;
      continue;
    }

    if (msg.role === MessageRole.ASSISTANT || msg.role === MessageRole.MASTER_VIBE_PROMPT) {
      const { toolCalls, toolResults, nextIndex } = collectToolCallsAndResults(messages, i + 1);
      const content = (msg.content || "").trim();

      // Skip empty assistant stubs left over from send setup / aborted turns so they
      // don't pollute queued drain history as orphan assistant messages.
      if (!content && toolCalls.length === 0) {
        i = nextIndex;
        continue;
      }

      result.push({
        role: "assistant",
        content: textContent(content),
        ...(toolCalls.length > 0 && { tool_calls: toolCalls }),
      });

      for (const { id, content: toolContent } of toolResults) {
        result.push({
          role: "tool",
          content: toolContent,
          tool_call_id: id,
        });
      }

      i = nextIndex;
      continue;
    }

    i += 1;
  }

  return result;
};
